import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking");
    const [tx, setTx] = useState(null);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let attempts = 0;
        let timeoutId;
        const poll = async () => {
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                setTx(data);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                attempts += 1;
                if (attempts >= 8) {
                    setStatus("timeout");
                    return;
                }
                timeoutId = setTimeout(poll, 2000);
            } catch {
                setStatus("error");
            }
        };
        poll();
        return () => clearTimeout(timeoutId);
    }, [sessionId]);

    const brand = tx?.metadata?.brand || "framework";
    const homeUrl = brand === "stringtheory" ? "/stringtheory" : "/framework";
    const isSwingAi = tx?.metadata?.item_type === "swing_ai";
    const swingUrl = brand === "stringtheory" ? "/stringtheory/swing-analysis" : "/framework/swing-analysis";

    return (
        <div className="min-h-screen bg-[#060606] text-white flex items-center justify-center p-6" data-testid="checkout-success-page">
            <div className="max-w-lg w-full border border-white/10 bg-[#0f0f10] p-10 text-center">
                {status === "checking" && (
                    <>
                        <Loader2 className="animate-spin mx-auto text-[#a6ce39]" size={40} />
                        <h1 className="text-2xl mt-6 font-semibold">Confirming your payment…</h1>
                        <p className="text-white/50 mt-3 text-sm">This usually takes a few seconds.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle2 className="mx-auto text-[#a6ce39]" size={48} />
                        <h1 className="text-3xl mt-6 font-semibold" data-testid="checkout-success-title">Payment received</h1>
                        <p className="text-white/60 mt-3">
                            {tx?.metadata?.description ? `Thanks for your purchase of "${tx.metadata.description}".` : "Thank you for your purchase."}
                        </p>
                        {tx?.amount_total != null && (
                            <p className="mt-2 text-sm text-white/50">
                                Charged: ${(tx.amount_total / 100).toFixed(2)} {String(tx.currency || "usd").toUpperCase()}
                            </p>
                        )}
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                            {isSwingAi ? (
                                <Link to={`${swingUrl}?paid=${sessionId}`} className="px-6 py-3 bg-[#a6ce39] text-black font-semibold" data-testid="checkout-swing-continue">
                                    Continue to Swing Analysis
                                </Link>
                            ) : (
                                <Link to={homeUrl} className="px-6 py-3 bg-[#a6ce39] text-black font-semibold" data-testid="checkout-back-home">
                                    Back to site
                                </Link>
                            )}
                        </div>
                    </>
                )}
                {(status === "expired" || status === "timeout" || status === "error") && (
                    <>
                        <XCircle className="mx-auto text-red-400" size={48} />
                        <h1 className="text-2xl mt-6 font-semibold">Payment not confirmed</h1>
                        <p className="text-white/60 mt-3 text-sm">
                            {status === "expired" ? "This session has expired." : "We couldn't confirm payment yet. If you were charged, please contact us."}
                        </p>
                        <Link to={homeUrl} className="inline-block mt-6 px-6 py-3 border border-white/30">Back to site</Link>
                    </>
                )}
            </div>
        </div>
    );
}
